import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { SectionHeader, Reveal } from "../Reveal";

const cityKey = (name) => (name || "").split(/[\s&]/)[0].toLowerCase();

const DEFAULT_CITIES = [
  {
    name: "Cannes",
    description: "La Croisette, festivals mythiques et penthouses en bord de mer",
    image: "https://images.unsplash.com/photo-1699264859754-9a9d316a30ba?crop=entropy&cs=srgb&fm=jpg&q=85",
    count: "24 biens",
  },
  {
    name: "Antibes & Cap d'Antibes",
    description: "Presqu'île préservée, criques sauvages et demeures de légende",
    image: "https://images.unsplash.com/photo-1707760466115-08aca18436b3?crop=entropy&cs=srgb&fm=jpg&q=85",
    count: "18 biens",
  },
  {
    name: "Nice",
    description: "Baie des Anges, collines du Mont-Boron et appartements haussmanniens",
    image: "https://images.unsplash.com/photo-1627458061789-b2b03b67e7ba?crop=entropy&cs=srgb&fm=jpg&q=85",
    count: "32 biens",
  },
  {
    name: "Mougins",
    description: "Village d'artistes, gastronomie et mas provençaux de grand standing",
    image: "https://images.unsplash.com/photo-1730126680640-bb6c3549d775?crop=entropy&cs=srgb&fm=jpg&q=85",
    count: "15 biens",
  },
  {
    name: "Saint-Jean-Cap-Ferrat",
    description: "L'écrin confidentiel des plus luxueuses propriétés au monde",
    image: "https://images.unsplash.com/photo-1693934304978-22274e6a3276?crop=entropy&cs=srgb&fm=jpg&q=85",
    count: "9 biens",
  },
  {
    name: "Juan-les-Pins",
    description: "Pinèdes séculaires, plages de sable fin et style riviera jazzy",
    image: "https://images.unsplash.com/photo-1677182162207-7cd011520525?crop=entropy&cs=srgb&fm=jpg&q=85",
    count: "12 biens",
  },
  {
    name: "Saint-Raphaël",
    description: "Roches rouges de l'Estérel et villas Belle Époque pieds dans l'eau",
    image: "https://images.unsplash.com/photo-1578240749366-a1d3b6c39cb4?crop=entropy&cs=srgb&fm=jpg&q=85",
    count: "11 biens",
  },
];

export default function Cities() {
  const [CITIES, setCITIES] = useState(DEFAULT_CITIES);
  const [counts, setCounts] = useState({});
  useEffect(() => {
    api.get("/cities").then((r) => {
      if (r.data?.length) setCITIES(r.data);
    }).catch(() => {});
    api.get("/properties").then((r) => {
      const c = {};
      r.data.forEach((p) => {
        const key = cityKey(p.location || "");
        if (key) c[key] = (c[key] || 0) + 1;
      });
      setCounts(c);
    }).catch(() => {});
  }, []);
  return (
    <section data-testid="cities-section" className="overflow-hidden py-12 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeader
          eyebrow="La Côte d'Azur, notre terrain de jeu"
          title="Vivre la Côte d'Azur"
          sub="De Cannes à Mandelieu, de Nice au Cap Ferrat — nous connaissons chaque quartier, chaque vue, chaque adresse."
        />
      </div>
      <div className="no-scrollbar mt-14 flex snap-x snap-mandatory gap-6 overflow-x-auto px-4 pb-4 sm:px-8 lg:px-16">
        {CITIES.map((city, i) => (
          <motion.div
            key={city.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="h-96 w-[280px] shrink-0 snap-start sm:w-[320px]"
          >
            <Link
              to={`/biens?location=${encodeURIComponent(cityKey(city.name))}`}
              data-testid={`city-card-${city.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="group relative block h-full overflow-hidden rounded-2xl"
            >
              <img
                src={city.image}
                alt={city.name}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                  {(counts[cityKey(city.name)] || 0) > 0
                    ? `${counts[cityKey(city.name)]} bien${counts[cityKey(city.name)] > 1 ? "s" : ""}`
                    : "Sur demande"}
                </span>
                <h3 className="mt-1.5 font-serif text-2xl font-semibold text-white">{city.name}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-white/75 opacity-0 transition-all duration-500 group-hover:opacity-100">
                  {city.description}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-gold opacity-0 transition-all duration-500 group-hover:opacity-100">
                  Voir les biens <ArrowUpRight size={13} />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
      <div className="mx-auto mt-6 max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal>
          <Link
            to="/biens"
            data-testid="cities-explore-link"
            className="group inline-flex items-center gap-2 text-sm font-medium text-azur transition-colors hover:text-terracotta"
          >
            Explorer les biens par secteur
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
